// Anita Week 2 / Day 1 — Time, Plan and Daily Routine (U.S. Grade 3).
// Math: two-step time problems, table reading vs 60-min limit, 1/2 and 1/4
// of free time. Russian RU Grade 1–2. Logic: which plan is better before bed.
// SAFETY: paper simulations only. No screen-time recommendations.

const day1 = {
  id: 'day-1',
  studentId: 'anita',
  weekId: 'week-2',
  dayId: 'day-1',
  status: 'active',
  title: 'Время, план и распорядок / Time, Plan and Daily Routine',
  shortTitle: 'Время и план / Time and Plan',
  theme:
    'План помогает не спешить. Сначала важное, потом отдых. / A plan helps you not rush. Important things first, then rest.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни приносит часы и маленький план дня. Сегодня Анита считает минуты, читает таблицу дел и выбирает порядок. / Nimny brings a clock and a tiny day plan. Today Anita counts minutes, reads a chores table, and picks an order.',
  goals: [
    'Складывать минуты и переводить их в часы и минуты. / Add minutes and convert to hours and minutes.',
    'Решать двухшаговые задачи о времени до сна. / Solve two-step bedtime word problems.',
    'Читать таблицу дел и сравнивать с лимитом времени. / Read a chores table and compare with a time limit.',
    'Работать с дробями 1/2 и 1/4 от свободного времени. / Use 1/2 and 1/4 of free time.',
    'Сравнить два плана и выбрать лучший перед сном. / Compare two plans and pick the smarter one before bed.'
  ],
  vocabulary: [
    { term: 'время / time', definition: 'то, что показывают часы. / what a clock shows.' },
    { term: 'план / plan', definition: 'порядок дел. / the order of things to do.' },
    { term: 'утро / morning', definition: 'начало дня. / the start of the day.' },
    { term: 'вечер / evening', definition: 'конец дня. / the end of the day.' },
    { term: 'читать / to read', definition: 'смотреть слова и понимать. / to look at words and understand.' },
    { term: 'играть / to play', definition: 'весело проводить время. / to spend time having fun.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: время, план, утро, вечер, читать, играть.',
      ruPrimaryText: 'Прочитай по-русски: время, план, утро, вечер, читать, играть.',
      enHelpText: 'Read in Russian: time, plan, morning, evening, to read, to play.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения 6 русских слов. / Attempted Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. План помогает не спешить.\n2. Утром я учусь, вечером отдыхаю.\n3. Я считаю минуты и часы.\n4. Сначала важное, потом игра.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про план дня.',
      enHelpText:
        '1. A plan helps me not rush.\n2. I study in the morning, rest in the evening.\n3. I count minutes and hours.\n4. Important things first, then play.',
      answer: 'Попытка чтения 4 русских фраз. / Attempted Russian reading.'
    },
    {
      id: 'math-1-total-minutes',
      type: 'math',
      title: 'Math 1 — Сколько времени ушло / How Much Time',
      prompt:
        'Анита читала 18 минут, рисовала 25 минут и делала задания 17 минут.\nСколько минут она занималась всего?\nСколько это часов и минут?\n\nЗапиши ответ через /: минуты / часы. Например: 60 / 1 час.',
      ruPrimaryText:
        '18 + 25 + 17 = ?. Переведи минуты в часы. Ответ через /: минуты / часы.',
      enHelpText:
        'Anita read 18 min, drew 25 min, did tasks 17 min. Total minutes? Convert to hours and minutes. Format as minutes / hours.',
      helper: 'Сначала сумма, потом перевод в часы. 60 минут = 1 час. / Sum first, then convert. 60 minutes = 1 hour.',
      answer: '60 / 1 час'
    },
    {
      id: 'math-2-bedtime-remainder',
      type: 'math',
      title: 'Math 2 — Осталось до сна / Time Until Bed',
      prompt:
        'До сна осталось 90 минут.\nАнита уже потратила 35 минут на чтение и 20 минут на игру.\nСколько минут осталось на душ и подготовку ко сну?',
      ruPrimaryText: '90 минут до сна. Потратила 35 + 20. Сколько осталось?',
      enHelpText: '90 minutes until bedtime. She already spent 35 + 20. How many remain?',
      helper: 'Сначала сумма потраченного, потом 90 минус сумма. / Sum spent first, then subtract from 90.',
      answer: '35',
      unit: 'минут / minutes'
    },
    {
      id: 'math-3-chores-table',
      type: 'math',
      title: 'Math 3 — Таблица дел / Chores Table',
      prompt:
        'Таблица:\nчтение — 20 минут\nрисование — 15 минут\nуборка — 10 минут\nигра — 25 минут\n\nАнита выбрала чтение, уборку и игру.\nСколько минут займут эти дела?\nЕсли у неё есть 1 час, хватит ли времени?\n\nЗапиши ответ через /: минуты / да или нет. Например: 55 / хватит.',
      ruPrimaryText:
        '20 + 10 + 25 = ?. 1 час = 60 минут. Хватит ли?',
      enHelpText:
        'Use the table: reading 20, cleanup 10, play 25. 1 hour = 60 minutes. Is it enough? Format as minutes / yes-or-no.',
      helper: 'Сначала сумма выбранных дел, потом сравни с 60. / Sum the picked items, then compare to 60.',
      answer: '55 / хватит'
    },
    {
      id: 'math-4-half-and-quarter-time',
      type: 'math',
      title: 'Math 4 — Половина и четверть времени / Half and a Quarter of Time',
      prompt:
        'У Аниты есть 80 минут свободного времени.\nПоловину она хочет потратить на чтение.\n1/4 времени — на рисование.\nСколько минут останется на игру?',
      ruPrimaryText: '1/2 от 80 + 1/4 от 80. Сколько останется на игру?',
      enHelpText: '80 minutes free. 1/2 for reading + 1/4 for drawing. How many remain for play?',
      helper: '1/2 от 80 = 40. 1/4 от 80 = 20. Сложи и вычти. / 1/2 of 80 = 40. 1/4 of 80 = 20. Sum and subtract.',
      answer: '20',
      unit: 'минут / minutes'
    },
    {
      id: 'logic-plan-a-vs-b',
      type: 'text',
      title: 'Логика — какой план лучше перед сном / Logic — Which Plan Before Bed',
      prompt:
        'План A: сначала игра, потом уроки.\nПлан B: сначала уроки, потом игра.\nКакой план лучше перед сном и почему?\nОтветь 1–2 предложениями по-русски.',
      ruPrimaryText: 'A: игра потом уроки. B: уроки потом игра. Какой лучше и почему?',
      enHelpText: 'A: play first, then homework. B: homework first, then play. Which is better before bed and why? Answer in 1–2 Russian sentences.',
      placeholder: 'План B лучше, потому что ...',
      answer:
        'Принимается ответ, где «План B» выбран с объяснением: «после игры трудно сосредоточиться», «уроки лучше делать на свежую голову», «после уроков можно спокойно отдохнуть».'
    },
    {
      id: 'spelling-time',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши: время, план, утро, вечер.\nВставь буквы: вре_я, пл_н, ут_о, веч_р.',
      ruPrimaryText: 'Напиши слова и вставь буквы.',
      enHelpText: 'Write the words and fill in the missing letters: время, план, утро, вечер.',
      answer: 'время, план, утро, вечер.'
    },
    {
      id: 'creative-my-evening',
      type: 'text',
      title: 'Мой вечер / My Evening',
      prompt:
        'Нарисуй маленький план своего вечера на 90 минут.\nЗаполни:\n— чтение ___ мин\n— игра ___ мин\n— подготовка ко сну ___ мин\nВсего: 90 мин.',
      ruPrimaryText: 'Нарисуй и заполни план вечера на 90 минут.',
      enHelpText: 'Draw and fill in your 90-minute evening plan: reading, play, bedtime prep.',
      answer: 'Open-ended. Принимается, если сумма равна 90 минут и есть все 3 категории.'
    },
    {
      id: 'speech-explain-plan',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: «План помогает мне ..., потому что ...».',
      ruPrimaryText: 'Скажи по-русски одну фразу про план.',
      enHelpText: 'Say in Russian: "A plan helps me ... because ...".',
      answer: 'Принимаются любые осмысленные продолжения фразы.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши 6 слов дня.',
    '2. Реши Math 1–4 с шагами в тетради.',
    '3. Объясни, какой план лучше перед сном.',
    '4. Напиши 4 слова на правописание.',
    '5. Нарисуй план вечера на 90 минут.',
    '6. Скажи родителю: «План помогает мне ..., потому что ...».'
  ],
  printWorksheet: {
    title: 'Время, план и распорядок / Time, Plan and Daily Routine',
    subtitle: 'Неделя 2 · День 1 / Week 2 · Day 1',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита учится планировать день и считать минуты. / Mission: Anita learns to plan her day and count minutes.' },
          { type: 'list', items: [
            'Слова дня: время, план, утро, вечер, читать, играть. / Words of the day.',
            'Маленькое чтение — 4 короткие фразы по-русски про план (см. экран). / Short Russian reading.',
            'English help мелким шрифтом — только если трудно. / English help in small print.'
          ] }
        ]
      },
      {
        title: 'Математика времени / Time Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги. 60 минут = 1 час. / Show steps. 60 minutes = 1 hour.' },
          { type: 'list', items: [
            'Math 1. 18 + 25 + 17 = ___ минут. ___ минут = ___ часов ___ минут. / Total minutes and conversion.',
            'Math 2. 35 + 20 = ___; 90 − ___ = ___ минут на душ и сон. / Bedtime remainder.',
            'Math 3. Таблица: чтение 20, уборка 10, игра 25. Сумма = ___. 1 час = 60 минут. Хватит ли? ___ / Chores table.',
            'Math 4. 1/2 от 80 = ___; 1/4 от 80 = ___; на игру = 80 − ___ − ___ = ___ минут. / Half and a quarter of free time.',
            'Логика: какой план лучше перед сном — A (игра первой) или B (уроки первыми)? Ответ 1–2 предложения. / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, мой вечер-проект и parent check / Spelling, My Evening-Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши время, план, утро, вечер. Вставь буквы: вре_я, пл_н, ут_о, веч_р. / Spelling.',
            'Нарисуй или составь мой вечер-проект на 90 минут: чтение ___ мин, игра ___ мин, подготовка ко сну ___ мин. Всего 90. / Draw or build a 90-min evening plan.',
            'Скажи родителю: «План помогает мне ..., потому что ...». / Say in Russian to a parent.',
            'Parent Check: сумма минут = 60, 1 час = 60 минут, дроби 1/2 и 1/4 от 80, объяснение плана перед сном. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита считает минуты, переводит в часы, читает таблицу дел и выбирает порядок.',
    mathGoal: 'U.S. Grade 3: суммы из трёх слагаемых + перевод 60 минут в час, двухшаговая задача о времени до сна, чтение таблицы vs лимит 60 мин, дроби 1/2 и 1/4 от 80.',
    russianGoal: 'Прочитать слова: время, план, утро, вечер, читать, играть.',
    readingWritingGoal: 'Прочитать 4 короткие фразы, написать 4 слова, нарисовать план вечера.',
    practicalGoal: 'Сравнить два плана и выбрать лучший перед сном.',
    whatThisLessonTeaches:
      'Анита учится считать время в две операции, переводить минуты в часы и сравнивать сумму дел с лимитом 1 час.',
    keyMessage:
      'Умный план = «важное сначала, отдых потом». 60 минут = 1 час, и план помогает увидеть, влезают ли все дела.',
    howToExplain:
      'Возьмите бумажные часы и листок с делами. Складывайте минуты и сравнивайте с лимитом.',
    exampleDialogue:
      'Parent: Сколько минут ушло у тебя на чтение и рисование вместе?\nChild: 18 + 25 = 43.\nParent: А с заданиями?\nChild: 43 + 17 = 60.\nParent: Это сколько часов?\nChild: 1 час.',
    readingSupport: 'Слова «вечер», «утро» читать по слогам.',
    russianSpeechGoal: 'Анита говорит: «План помогает мне не спешить, потому что …».',
    mathAnswerKey:
      'Math 1: 18 + 25 + 17 = 60 минут = 1 час.\nMath 2: 35 + 20 = 55; 90 − 55 = 35 минут.\nMath 3: 20 + 10 + 25 = 55; 60 − 55 = 5; хватит.\nMath 4: 1/2 от 80 = 40; 1/4 от 80 = 20; 80 − 40 − 20 = 20 минут на игру.',
    spellingAnswerKey: 'время\nплан\nутро\nвечер',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 6 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про план дня.\nSolution: Каждая фраза — отдельно.\nParent note: Английский — только для смысла.' },
      { taskId: 'math-1-total-minutes', answer: 'Answer: 60 минут = 1 час.\nSolution: 18 + 25 + 17 = 60; 60 минут = 1 час.\nParent note: Сложение трёх чисел и перевод минут в часы.' },
      { taskId: 'math-2-bedtime-remainder', answer: 'Answer: 35 минут.\nSolution: 35 + 20 = 55; 90 − 55 = 35.\nParent note: Двухшаговая задача: сначала потраченное, потом остаток.' },
      { taskId: 'math-3-chores-table', answer: 'Answer: 55 минут; хватит.\nSolution: 20 + 10 + 25 = 55; 1 час = 60; 55 < 60.\nParent note: Ребёнок читает таблицу и сравнивает с лимитом.' },
      { taskId: 'math-4-half-and-quarter-time', answer: 'Answer: 20 минут.\nSolution: 1/2 от 80 = 40; 1/4 от 80 = 20; 80 − 40 − 20 = 20.\nParent note: Простые дроби от числа.' },
      { taskId: 'logic-plan-a-vs-b', answer: 'Answer: умнее План B (уроки первыми).\nSolution: После игры труднее сосредоточиться; уроки на свежую голову; после уроков можно спокойно отдохнуть.\nParent note: Принимается любое объяснение с этими идеями.' },
      { taskId: 'spelling-time', answer: 'Answer: время, план, утро, вечер.\nSolution: Вставленные буквы: м, а, р, е.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-my-evening', answer: 'Answer: план вечера на 90 минут с тремя категориями.\nSolution: Любая сумма категорий = 90.\nParent note: Главное — все три категории и правильная арифметика.' },
      { taskId: 'speech-explain-plan', answer: 'Answer: одна осмысленная русская фраза про план.\nSolution: «План помогает мне не спешить.»\nParent note: Главное — мысль.' }
    ],
    check: [
      'Анита прочитала русские слова и 4 фразы.',
      'Анита решила Math 1–4 с показом шагов.',
      'Анита выбрала умный план перед сном и объяснила.',
      'Анита написала 4 слова и составила план вечера.',
      'Анита сказала фразу про план.'
    ],
    spellingCheck: 'Проверить буквы в словах время, план, утро, вечер.',
    readingCheck: 'Засчитывать попытку чтения; помогать по слогам.',
    practicalCheck: 'Анита делит вечер на 3 категории и складывает в 90 минут.',
    drawingCriteria: 'План вечера имеет 3 категории, и сумма = 90 минут.',
    commonMistakes: [
      '«Минута = час.» Правильно: 60 минут = 1 час.',
      '«Сначала игра.» Правильно: важное первым, отдых потом.',
      '«1/4 = 4.» Правильно: 1/4 от 80 = 20 (80 ÷ 4).'
    ],
    discussionPrompts: [
      'Зачем нужен план дня?',
      'Что лучше: длинный отдых после или маленький отдых между делами?',
      'Что изменится, если у тебя есть только 30 минут?'
    ],
    ifEasy: 'У Аниты 120 минут. 1/3 — чтение, 1/4 — рисование. Остальное игра. Сколько на игру? (120 − 40 − 30 = 50.)',
    ifDifficult: 'Оставьте Math 1 и Math 3. Math 3 разберите по таблице вслух.',
    rewardGuidance: 'Базовая награда 3 нима. Бонус за самостоятельный перевод минут в часы или за хороший выбор плана.',
    parentTheoryCard: {
      title: 'Время и план дня (Day 1) / Time and Day Plan',
      purpose: 'Научить ребёнка думать про время как про ограниченный ресурс и видеть порядок дел.',
      keyMessage: 'Сначала важное, потом отдых. 60 минут = 1 час.',
      parentScript:
        'Покажите ребёнку, как мы планируем вечер: сначала уроки, потом игра. Считайте минуты вслух.',
      practiceSteps: [
        'Прочитайте слова дня вместе.',
        'Решите Math 1–4, проговаривая шаги.',
        'Сравните План A и План B перед сном.',
        'Заполните 90-минутный план вечера.'
      ],
      doNotSay: [
        'Не говорите «у тебя слишком много времени».',
        'Не сравнивайте с другими детьми.',
        'Не ругайте за неправильное чтение часов.'
      ],
      paperOnlyNotes:
        'Это бумажный план, не реальный жёсткий распорядок. Реальные минуты подскажет родитель.'
    },
    parentOnlyNotes: 'Не превращайте план в наказание. Это инструмент.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'minute-hour-conversion', label: 'Бонус за самостоятельный перевод минут в часы', amount: 1 },
      { id: 'good-plan-explanation', label: 'Бонус за хорошее объяснение плана', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words', 'reading-sentences',
      'math-1-total-minutes', 'math-2-bedtime-remainder',
      'math-3-chores-table', 'math-4-half-and-quarter-time',
      'logic-plan-a-vs-b', 'spelling-time',
      'creative-my-evening', 'speech-explain-plan'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-2-day-2'
};

export default day1;
